import { useState, useMemo } from "react";
import { Avatar } from "antd";
import { ReactComponent as InstagramIcon } from "assets/images/instagram-icon.svg";
import { ReactComponent as TikTokIcon } from "assets/images/tiktok-icon.svg";
import { formatNumberKMBT } from "pages/influencers/InfluencerCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import "./CreatorCard.scss";

type Creator = {
  foodieId?: string;
  top3PostsCoverImgUrl?: string[];
  avatarUrl?: string;
  username?: string;
  name?: string;
  categoriesIA?: string[];
  instagramFollowersCount?: number;
  tiktokFollowersCount?: number;
  instagramEngagementRate?: number;
  tiktokEngagementRate?: number;
  avgLikes?: number;
  inviteAvailable?: boolean;
  invited?: boolean;
};

type FakeCreatorCardProps = {
  creator: Creator;
  onClick?: () => void;
  index?: number;
};

const FakeCreatorCard: React.FC<FakeCreatorCardProps> = ({
  creator,
  onClick,
  index = 0,
}) => {
  const animationDelay = `${Math.min(index * 60, 600)}ms`;

  // Local platform state to keep social buttons interactive
  const [platform, setPlatform] = useState<"ig" | "tt">(() => {
    const ig = creator?.instagramFollowersCount ?? 0;
    const tt = creator?.tiktokFollowersCount ?? 0;
    return ig >= tt ? "ig" : "tt";
  });

  const stats = useMemo(() => {
    const followers =
      platform === "ig"
        ? (creator?.instagramFollowersCount ?? 0)
        : (creator?.tiktokFollowersCount ?? 0);
    const engagement =
      platform === "ig"
        ? (creator?.instagramEngagementRate ?? 0)
        : (creator?.tiktokEngagementRate ?? 0);
    const avgLikes = creator?.avgLikes ?? 0;
    return { followers, engagement, avgLikes };
  }, [platform, creator]);

  const colorStyleForTag = (tag: string) =>
    ({
      backgroundColor: "rgba(0,0,0,0.04)",
      color: "#333333",
    }) as React.CSSProperties;

  const invited = creator?.invited ?? false;

  return (
    <div
      className="creator-card is-blurred"
      role="article"
      aria-label={`Open creator ${creator.username ?? "profile"}`}
      style={{ "--cc-animation-delay": animationDelay } as React.CSSProperties}
      onClick={() => (onClick ? onClick() : null)}
    >
      <div className="creator-card__content">
        <div className="creator-card__top">
          <div className="creator-card__images">
            {(creator.top3PostsCoverImgUrl?.length || 0) > 0 ? (
              creator.top3PostsCoverImgUrl?.map((src, idx) => (
                <div
                  key={idx}
                  className={`creator-card__image creator-card__image--${idx}`}
                >
                  <img src={src} alt={`post-${idx}`} />
                </div>
              ))
            ) : (
              <div>
                <p
                  style={{
                    padding: 20,
                    color: "#999999",
                    textAlign: "center",
                  }}
                >
                  Influencer profile is being enriched
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="creator-card__body">
          {/* Heart is present visually but has no action */}
          <button
            className={`creator-card__heart`}
            aria-label={"Favoritar"}
            type="button"
            disabled
          >
            {/* simple heart placeholder */}
            <span style={{ fontSize: 16, opacity: 0.85 }}>❤</span>
          </button>
          <div className="creator-card__header">
            <Avatar
              src={creator.avatarUrl}
              className="creator-card__avatar"
              alt={creator.username ?? "avatar"}
            />

            <div className="creator-card__info">
              <div className="creator-card__username">
                <span
                  className="creator-card__username-text"
                  title={creator.name ?? "Creator"}
                >
                  {creator.name ?? "Creator"}
                </span>
              </div>
              <div
                className="creator-card__tags"
                onScroll={(e) => e.stopPropagation()}
              >
                {creator?.categoriesIA?.slice(0, 6)?.map((t) => (
                  <span
                    key={t}
                    className="creator-card__tag"
                    style={colorStyleForTag(t)}
                  >
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div
            className="creator-card__social"
            role="tablist"
            aria-label="Platform selector"
          >
            <div className="creator-card__buttons">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPlatform("ig");
                }}
                aria-pressed={platform === "ig"}
                aria-label="Instagram"
                className={`creator-card__social-btn creator-card__social-btn--ig ${
                  platform === "ig" ? "is-active" : ""
                }`}
              >
                <InstagramIcon fill="#db2777" />
                <span className="creator-card__social-text">IG</span>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPlatform("tt");
                }}
                aria-pressed={platform === "tt"}
                aria-label="TikTok"
                className={`creator-card__social-btn creator-card__social-btn--tt ${
                  platform === "tt" ? "is-active" : ""
                }`}
              >
                <TikTokIcon stroke="#000" fill="#000" />
                <span className="creator-card__social-text">TT</span>
              </button>
            </div>
          </div>

          <div className="creator-card__stats">
            <div className="creator-card__stat">
              <div className="creator-card__stat-value">
                {formatNumberKMBT(stats.followers || Math.random() * 1000000)}
              </div>
              <div className="creator-card__stat-label">Followers</div>
            </div>
            <div className="creator-card__stat">
              <div className="creator-card__stat-value">
                {stats.engagement || (Math.random() * 10).toFixed(1)}%
              </div>
              <div className="creator-card__stat-label">Engagement</div>
            </div>

            <div className="creator-card__stat">
              <div className="creator-card__stat-value">
                {formatNumberKMBT(stats.avgLikes || Math.random() * 100000, 0)}
              </div>
              <div className="creator-card__stat-label">Avg. Likes</div>
            </div>
          </div>

          <button
            className={`creator-card__action ${invited ? "is-invited" : ""}`}
            disabled
            hidden={!creator.inviteAvailable}
            type="button"
          >
            {invited ? "Invite Sent" : "Invite"}
          </button>
        </div>
      </div>

      <div className="creator-card__overlay" aria-hidden="true">
        <div className="creator-card__lock" title="Conteúdo protegido">
          <FontAwesomeIcon icon={faLock} />
        </div>
      </div>
    </div>
  );
};

export default FakeCreatorCard;
